import React, { useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { sendTrackingEvent } from "../../../helpers/tracking_management";
import {
  DialogContent,
  IconButton,
  InputLabel,
  MenuItem,
  Typography,
  useMediaQuery,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Link from "@mui/material/Link";
import { updateLanguageDetailsDialog } from "../../../redux/actions/dialog_action";
import { Trans, useTranslation } from "react-i18next";
import {
  ActionsRight,
  AddLanguageContainer,
  AddLanguageFormControl,
  AddLanguageSection,
  AddLanguageSelect,
  CloseDialogIconButton,
  DeleteOutlineIcon,
  DialogContainer,
  DoneButton,
  DontShowAgainButton,
  getAddLanguageMenuProps,
  GridHeaderLanguageCell,
  GridHeaderLevelCell,
  GridHeaderSpacerCell,
  GridRowCellCentered,
  GridRowLanguageCell,
  LanguageDialogActions,
  LanguageDialogTitle,
  LanguageGrid,
  MobileLanguageCard,
  MobileLanguageCardHeader,
  MobileLanguageList,
  MobileRadioGroup,
  MobileRadioOption,
  ProficiencyErrorText,
  ProficiencyRadio,
} from "./styles";
import * as constants from "../../../helpers/constant";
import { saveUserLanguages } from "../../../redux/actions/user_action";
import * as types from "../../../redux/types/user_type";
import * as config from "../../../config/config";
import useTriggerSnackbar from "../../../hooks/useTriggerSnackbar";
import { useRouter } from "next/router";
import { useLayoutEffect } from "react";
import {
  getLanguageLabel as getTranslatedLanguageLabel,
  getProficiencyLabel as getTranslatedProficiencyLabel,
} from "../../../helpers/language_labels";

const DEFAULT_LANGUAGES = ["Malay", "English", "Mandarin", "Tamil", "Japanese", "Korean"];
const MAX_LANGUAGES = 7;

const proficiencyLevels = {
  CONVERSATIONAL: "Conversational",
  PROFESSIONAL: "Professional",
  NATIVE: "Native",
  NA: "N/A",
};

function LanguageProficiencyDialog({
  onDecision,
  open: openProp,
  onClose: onCloseProp,
  onDontShowAgain,
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const router = useRouter();
  const openFromRedux = useSelector(
    (state) => state.dialog.showLanguageDetailsDialog
  );
  const open = openProp ?? openFromRedux;

  const titleRef = useRef(null);
  const selectRef = useRef(null);
  const selectWrapperRef = useRef(null);
  const [menuGapHeight, setMenuGapHeight] = useState(200);
  const [menuWidth, setMenuWidth] = useState(undefined)

  const [totalLanguages, setTotalLanguages] = useState([
    { language: "Malay", proficiency_level: proficiencyLevels.NA },
    { language: "English", proficiency_level: proficiencyLevels.NA },
    { language: "Mandarin", proficiency_level: proficiencyLevels.NA },
    { language: "Tamil", proficiency_level: proficiencyLevels.NA },
    { language: "Japanese", proficiency_level: proficiencyLevels.NA },
    { language: "Korean", proficiency_level: proficiencyLevels.NA },
  ]);

  const [addLanguageValue, setAddLanguageValue] = useState(null);
  const isMobile = useMediaQuery("(max-width:640px)");

  const levels = useMemo(() => Object.values(proficiencyLevels), []);

  const getLanguageLabel = (language) => getTranslatedLanguageLabel(t, language);

  const getProficiencyLabel = (level) => getTranslatedProficiencyLabel(t, level);

  const [hasError, setHasError] = useState(false);

  const [triggerSnackbarFunc] = useTriggerSnackbar();
  const triggerToast = (message, severity = "info", customSeverityText) => {
    triggerSnackbarFunc({
      snackbarMessage: message,
      severity: severity,
      customSeverityText,
    });
  };

  const handleClose = () => {
    if (onCloseProp) return onCloseProp();
    dispatch(updateLanguageDetailsDialog(false));
  };

  const handleDontShowAgain = () => {
    onDontShowAgain?.();
    handleClose();
  };

  const handleUpdateLanguage = async () => {
    const hasEmptyProficiency = totalLanguages.some(
      (l) => !l.proficiency_level
    );

    if (hasEmptyProficiency) {
      setHasError(true);
      return;
    }

    setHasError(false);
    const latestLanguages = totalLanguages
      .filter((l) => l.proficiency_level !== proficiencyLevels.NA)
      .map((l) => ({
        language: l.language,
        proficiency_level: l.proficiency_level,
      }));

    const result = await dispatch(
      saveUserLanguages({
        canCancel: false,
        latestLanguages,
      })
    );

    if (result?.type === types.SAVE_USER_UPDATED_LANGUAGES) {
      sendTrackingEvent({ 
        event: "CE_language_proficiency_save"
      });
      triggerToast(
        <Trans
          i18nKey="jobs.language_proficiency.toast_saved"
          components={{
            link: (
              <Link
                href="/profile"
                underline="always"
                onClick={() => router.push("/profile")}
              />
            ),
          }}
        />,
        "success",
        t("jobs.language_proficiency.toast_success_title")
      );
      handleClose();
      onDecision?.("update", latestLanguages);
      return;
    }
  };

  const handleRemoveLanguage = (lang) => {
    if (DEFAULT_LANGUAGES.includes(lang)) return;
    setTotalLanguages((prev) => prev.filter((l) => l.language !== lang));
  };

  const handleSelectProficiency = (rowIndex, level) => {
    setTotalLanguages((prev) => {
      const next = [...prev];
      next[rowIndex] = { ...next[rowIndex], proficiency_level: level };
      return next;
    });
  };

  useLayoutEffect(() => {
    if (!open || !isMobile) return;

    const measure = () => {
      const titleEl = titleRef.current;
      const wrapperEl = selectWrapperRef.current;
      if (!titleEl || !wrapperEl) return;

      const titleRect = titleEl.getBoundingClientRect();

      const inputRoot =
        wrapperEl.querySelector(".MuiOutlinedInput-root") ||
        wrapperEl.querySelector(".MuiInputBase-root") ||
        wrapperEl;

      const inputRect = inputRoot.getBoundingClientRect();

      // height gap
      const gap = Math.floor(inputRect.top - titleRect.bottom - 20);
      setMenuGapHeight(Math.max(30, gap));

      // width (menu width = container width)
      setMenuWidth(Math.floor(inputRect.width));
    };

    const rafMeasure = () =>
      requestAnimationFrame(() => requestAnimationFrame(measure));

    rafMeasure();
    window.addEventListener("resize", rafMeasure);

    const dialogContent = document.querySelector(".MuiDialogContent-root");
    dialogContent?.addEventListener("scroll", rafMeasure, { passive: true });

    return () => {
      window.removeEventListener("resize", rafMeasure);
      dialogContent?.removeEventListener("scroll", rafMeasure);
    };
  }, [open, isMobile, totalLanguages.length]);

  return (
    <DialogContainer
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown
      aria-labelledby="language-proficiency-title"
    >
      <LanguageDialogTitle id="language-proficiency-title" ref={titleRef}>
        {t("jobs.language_proficiency.title")}
      </LanguageDialogTitle>

      <DialogContent dividers>
        <Typography variant="body1">
          {t("jobs.language_proficiency.subtitle")}
        </Typography>

        {isMobile && <Divider sx={{ my: 2 }} />}

        {isMobile ? (
          <MobileLanguageList>
            {totalLanguages.map((row, rowIndex) => {
              const lang = row.language;
              const canRemove = !DEFAULT_LANGUAGES.includes(lang);

              return (
                <MobileLanguageCard key={`${lang}-${rowIndex}`}>
                  <MobileLanguageCardHeader>
                    <Typography fontWeight={700}>
                      {getLanguageLabel(lang)}
                    </Typography>
                    {canRemove ? (
                      <IconButton
                        size="small"
                        aria-label={t("jobs.language_proficiency.remove", {
                          language: getLanguageLabel(lang),
                        })}
                        onClick={() => handleRemoveLanguage(lang)}
                      >
                        <DeleteOutlineIcon
                          src={
                            config.assetDomain + "/images/hiredly/remove.png"
                          }
                          alt="Remove"
                        />
                      </IconButton>
                    ) : null}
                  </MobileLanguageCardHeader>

                  <MobileRadioGroup
                    value={row.proficiency_level ?? ""}
                    onChange={(_, value) => {
                      handleSelectProficiency(rowIndex, value);
                      setHasError(false);
                    }}
                    name={`${lang}-proficiency`}
                  >
                    {levels.map((level) => (
                      <MobileRadioOption
                        key={`${lang}-${level}`}
                        value={level}
                        control={
                          <ProficiencyRadio
                            size="medium"
                            highlightError={
                              hasError && row.proficiency_level == null
                            }
                          />
                        }
                        label={getProficiencyLabel(level)}
                      />
                    ))}
                  </MobileRadioGroup>
                </MobileLanguageCard>
              );
            })}
          </MobileLanguageList>
        ) : (
          <LanguageGrid levelsCount={levels.length}>
            <GridHeaderLanguageCell>
              {t("jobs.language_proficiency.language_header")}
            </GridHeaderLanguageCell>
            {levels.map((level) => (
              <GridHeaderLevelCell key={level}>
                {getProficiencyLabel(level)}
              </GridHeaderLevelCell>
            ))}
            <GridHeaderSpacerCell />

            {totalLanguages.map((row, rowIndex) => {
              const lang = row.language;
              const canRemove = !DEFAULT_LANGUAGES.includes(lang);

              return (
                <React.Fragment key={`${lang}-${rowIndex}`}>
                  <GridRowLanguageCell>
                    {getLanguageLabel(lang)}
                  </GridRowLanguageCell>

                  {levels.map((level) => (
                    <GridRowCellCentered key={`${lang}-${level}`}>
                      <ProficiencyRadio
                        checked={row.proficiency_level === level}
                        onChange={() => {
                          handleSelectProficiency(rowIndex, level);
                          setHasError(false);
                        }}
                        value={level}
                        name={`${lang}-proficiency`}
                        size="medium"
                        highlightError={
                          hasError && row.proficiency_level == null
                        }
                      />
                    </GridRowCellCentered>
                  ))}

                  <GridRowCellCentered>
                    {canRemove ? (
                      <IconButton
                        size="small"
                        aria-label={t("jobs.language_proficiency.remove", {
                          language: getLanguageLabel(lang),
                        })}
                        onClick={() => handleRemoveLanguage(lang)}
                      >
                        <DeleteOutlineIcon
                          src={
                            config.assetDomain + "/images/hiredly/remove.png" 
                          }
                          alt="Remove"
                        />
                      </IconButton>
                    ) : null}
                  </GridRowCellCentered>
                </React.Fragment>
              );
            })}
          </LanguageGrid>
        )}

        {totalLanguages.length < MAX_LANGUAGES && (
          <AddLanguageSection>
            <AddLanguageFormControl fullWidth>
              <InputLabel id="add-language-label">
                {t("jobs.language_proficiency.add_language")}
              </InputLabel>
              <AddLanguageContainer ref={selectWrapperRef}>
                <AddLanguageSelect
                  labelId="add-language-label"
                  ref={selectRef}
                  MenuProps={getAddLanguageMenuProps(isMobile, menuGapHeight, menuWidth)}
                  label={t("jobs.language_proficiency.add_language")}
                  value={addLanguageValue ?? ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (!value) return;

                    setTotalLanguages((prev) => [
                      ...prev,
                      { language: value, proficiency_level: null },
                    ]);
                    setAddLanguageValue("");
                  }}
                >
                  {(constants.languages || [])
                    .filter(
                      (lang) => !totalLanguages.some((l) => l.language === lang)
                    )
                    .map((lang) => (
                      <MenuItem key={lang} value={lang}>
                        {getLanguageLabel(lang)}
                      </MenuItem>
                    ))}
                </AddLanguageSelect>
              </AddLanguageContainer>
            </AddLanguageFormControl>
          </AddLanguageSection>
        )}
      </DialogContent>

      <LanguageDialogActions>
        <DontShowAgainButton onClick={handleDontShowAgain}>
          {t("jobs.language_proficiency.dont_show_again")}
        </DontShowAgainButton>

        <ActionsRight>
          {hasError && (
            <ProficiencyErrorText variant="body2">
              {t("jobs.language_proficiency.error_select_proficiency")}
            </ProficiencyErrorText>
          )}
          <CloseDialogIconButton onClick={handleClose}>
            <CloseIcon />
          </CloseDialogIconButton>
          <DoneButton
            variant="contained"
            button_type={"black"}
            onClick={handleUpdateLanguage}
          >
            {t("jobs.language_proficiency.save")}
          </DoneButton>
        </ActionsRight>
      </LanguageDialogActions>
    </DialogContainer>
  );
}

export default LanguageProficiencyDialog;
